import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';
import camelcaseKeys from 'camelcase-keys';

export const createMessagePayload = (payload, message) => {
  const { content, cc_emails, bcc_emails, channel_type } = message;
  payload.append('message[content]', content);
  payload.append('message[channel_type]', channel_type);

  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
  if (message.private) payload.append('message[private]', 'true');
};

export const createConversationPayload = ({ params, contactId, files }) => {
  const { inboxId, message, sourceId, mailSubject, assigneeId } = params;
  const payload = new FormData();

  if (message) {
    createMessagePayload(payload, message);
  }

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }

  payload.append('inbox_id', inboxId);
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  if (mailSubject) {
    payload.append('additional_attributes[mail_subject]', mailSubject);
  }
  payload.append('assignee_id', assigneeId);

  return payload;
};

export const createWhatsAppConversationPayload = ({ params }) => {
  const { inboxId, message, contactId, sourceId, assigneeId } = params;

  const payload = {
    inbox_id: inboxId,
    contact_id: contactId,
    source_id: sourceId,
    message,
    assignee_id: assigneeId,
  };

  return payload;
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  if (isFromWhatsApp) {
    return createWhatsAppConversationPayload({ params });
  }
  return createConversationPayload({
    params,
    contactId,
    files,
  });
};

const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isFetchingMore: false,
    hasMore: false,
    meta: {},
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getAllConversationsByContactId: $state => id => {
    const records = $state.records[Number(id)] || [];
    return camelcaseKeys(records, { deep: true });
  },

  getFilteredConversationsByContactId: $state => (id, property, value) => {
    const records = $state.records[Number(id)] || [];
    const filtered = records.filter(
      conversation => conversation[property] === value
    );
    return camelcaseKeys(filtered, { deep: true });
  },
};

export const actions = {
  create: async ({ commit }, { params, isFromWhatsApp }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      // Create the basic payload
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      // Regular conversation creation
      const response = await ConversationApi.create(payload);
      const data = response.data;

      if (contactId) {
        commit(types.default.ADD_CONTACT_CONVERSATION, {
          id: contactId,
          data,
        });
      }

      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  get: async ({ commit }, { contactId, status = '' }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetching: true,
      hasMore: false,
    });
    try {
      const response = await ContactAPI.getConversations(contactId, 1, status);
      const data = response.data.payload.conversations || response.data.payload;

      commit('REPLACE_CONTACT_CONVERSATIONS_BY_STATUS_SLICE', {
        id: contactId,
        data: data,
        status: status,
      });

      if (response.data.meta) {
        commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
          hasMore:
            response.data.meta.current_page < response.data.meta.total_pages,
          meta: response.data.meta,
        });
      }
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
      throw new Error(error);
    }
  },
  getMore: async ({ commit }, { contactId, page, status = '' }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetchingMore: true,
    });
    try {
      const response = await ContactAPI.getConversations(
        contactId,
        page,
        status
      );
      const newConversations =
        response.data.payload.conversations || response.data.payload;

      commit('APPEND_CONTACT_CONVERSATIONS', {
        id: contactId,
        data: newConversations,
      });
      if (response.data.meta) {
        commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
          hasMore:
            response.data.meta.current_page < response.data.meta.total_pages,
          meta: response.data.meta,
        });
      }
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetchingMore: false,
      });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetchingMore: false,
      });
      throw new Error(error);
    }
  },
  updateConversation: ({ commit }, data) => {
    if (data.meta && data.meta.sender && data.meta.sender.id) {
      commit(types.default.ADD_CONTACT_CONVERSATION, {
        id: Number(data.meta.sender.id),
        data,
      });
    }
  },
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    $state.records = {
      ...$state.records,
      [id]: data,
    };
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    $state.records = {
      ...$state.records,
      [id]: updatedConversations,
    };
  },
  ['REPLACE_CONTACT_CONVERSATIONS_BY_STATUS_SLICE']: (
    $state,
    { id, data, status }
  ) => {
    let conversations = [...($state.records[id] || [])];
    let validStatuses = [];
    if (status === 'open') {
      validStatuses = ['received', 'open', 'pending'];
    } else if (status === 'resolved') {
      validStatuses = ['solved'];
    } else if (status === 'closed') {
      validStatuses = ['closed'];
    }

    if (validStatuses.length > 0) {
      conversations = conversations.filter(
        conv => !validStatuses.includes(conv.status)
      );
    }

    data.forEach(newConv => {
      const index = conversations.findIndex(conv => conv.id === newConv.id);
      if (index !== -1) {
        conversations[index] = { ...conversations[index], ...newConv };
      } else {
        conversations.push(newConv);
      }
    });

    $state.records = {
      ...$state.records,
      [id]: conversations,
    };
  },
  ['APPEND_CONTACT_CONVERSATIONS']: ($state, { id, data }) => {
    let conversations = [...($state.records[id] || [])];
    data.forEach(newConv => {
      const index = conversations.findIndex(conv => conv.id === newConv.id);
      if (index !== -1) {
        conversations[index] = { ...conversations[index], ...newConv };
      } else {
        conversations.push(newConv);
      }
    });
    $state.records = {
      ...$state.records,
      [id]: conversations,
    };
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    const { [id]: deletedRecord, ...remainingRecords } = $state.records;
    $state.records = remainingRecords;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
